import { createSlice, PayloadAction } from "@reduxjs/toolkit";

interface NavigationState {
  currentView: string;
  previousView: string;
  history: string[]; // Navigation history stack
  refreshTimestamp: number; // Timestamp that updates on navigation to trigger DataTable refresh
}

const initialState: NavigationState = {
  currentView: "dashboard",
  previousView: "",
  history: ["dashboard"], // Start with dashboard in history
  refreshTimestamp: Date.now(),
};

const navigationSlice = createSlice({
  name: "navigation",
  initialState,
  reducers: {
    navigateToView(
      state,
      action: PayloadAction<{
        view: string;
        rememberPrevious?: boolean;
        isBack?: boolean;
      }>
    ) {
      const { view, rememberPrevious, isBack } = action.payload;

      if (isBack) {
        // Going back - pop from history
        if (state.history.length > 1) {
          state.history.pop(); // Remove current view
          const previousInHistory = state.history[state.history.length - 1];
          state.currentView = view;
          state.previousView =
            state.history.length > 1
              ? state.history[state.history.length - 2]
              : "";
        }
      } else {
        // Forward navigation
        if (rememberPrevious) {
          state.previousView = state.currentView;
        }

        // Add to history if it's not the same as current view
        if (view !== state.currentView) {
          state.history.push(view);
          // Keep history manageable (max 50 entries)
          if (state.history.length > 50) {
            state.history.shift();
          }
        }

        state.currentView = view;
        // Set previousView from history
        if (state.history.length > 1) {
          state.previousView = state.history[state.history.length - 2];
        }
      }

      // Update refresh timestamp to trigger DataTable refresh
      state.refreshTimestamp = Date.now();
    },
    setPreviousView(state, action: PayloadAction<string>) {
      state.previousView = action.payload;
      // Also update history to maintain consistency
      if (state.history.length > 1) {
        state.history[state.history.length - 2] = action.payload;
      }
    },
    resetPreviousView(state) {
      state.previousView = "";
    },
    clearHistory(state) {
      state.history = [state.currentView];
      state.previousView = "";
    },
    /** Bump refreshTimestamp so summary lists / tables reload without changing route. */
    triggerDataRefresh(state) {
      state.refreshTimestamp = Date.now();
    },
  },
});

export const {
  navigateToView,
  setPreviousView,
  resetPreviousView,
  clearHistory,
  triggerDataRefresh,
} = navigationSlice.actions;

export default navigationSlice.reducer;
